import React from 'react';
import process from 'process';
import posthog from 'posthog-js';
import { PostHogProvider as Provider } from 'posthog-js/react';

// ---------------------------------------------------------------------------
// Which PostHog project do events go to?
//
// Chosen automatically from the hostname — nothing to configure, no env vars
// or flags needed to work locally:
//
//   localhost / 127.0.0.1 / *.local / anything with "staging" or "dev."
//       -> TESTING project (safe to experiment, build dashboards, break things)
//   everything else (app.refnow.co and all white-label domains)
//       -> PRODUCTION project
//
// Both keys are PostHog *publishable* project keys — they are designed to ship
// in client-side bundles and are not secrets.
// ---------------------------------------------------------------------------
const PRODUCTION_KEY = 'phc_EhCuQAUifoqif10jaJgw6MesVgpNUsXu0Dr8bhRv8GL';
const TESTING_KEY = 'phc_Br57fgMnXQ5gptWcBYHxK5yNzJKUpPwSF7j6NFZh32rP';

function isNonProductionHost(hostname) {
	if (!hostname) return false;
	return (
		hostname === 'localhost' ||
		hostname === '127.0.0.1' ||
		hostname === '::1' ||
		hostname.endsWith('.local') ||
		hostname.includes('staging') ||
		hostname.startsWith('dev.')
	);
}

function getPostHogKey() {
	// Optional escape hatch: POSTHOG_KEY at build time overrides the automatic
	// choice. Not needed day to day — see webpack.config.js.
	if (process.env.RN_API_BASE_URL) return TESTING_KEY;
	if (process.env.POSTHOG_KEY) return process.env.POSTHOG_KEY;

	try {
		return isNonProductionHost(window.location.hostname) ? TESTING_KEY : PRODUCTION_KEY;
	} catch (e) {
		return PRODUCTION_KEY;
	}
}

// ---------------------------------------------------------------------------
// Privacy: strip sensitive query-string params before anything leaves the
// browser. The form links carry a bearer token (?token=...) and prefill links
// can carry referee PII (name/email/telephone/company/jobtitle), which would
// otherwise ride along inside $current_url / $referrer on every event.
// ---------------------------------------------------------------------------
const SENSITIVE_PARAM_RE =
	/((?:token|inputtoken|email|name|fullname|telephone|phone|company|jobtitle)=)[^&#\s]+/gi;

export function redactSensitiveParams(value) {
	try {
		return String(value)
			.replace(SENSITIVE_PARAM_RE, '$1[redacted]')
			.replace(/(\/(?:exit-surveys?|surveys?)\/)[A-Za-z0-9_-]{43}(?=[/?#\s]|$)/gi, '$1[redacted]');
	} catch (e) {
		return value;
	}
}

function redactObjectValues(obj) {
	Object.keys(obj).forEach((key) => {
		const value = obj[key];
		if (typeof value === 'string') {
			obj[key] = redactSensitiveParams(value);
		} else if (value && typeof value === 'object' && !Array.isArray(value)) {
			redactObjectValues(value);
		}
	});
}

// Runs on every captured event just before it is queued for sending.
function beforeSend(captureResult) {
	try {
		if (captureResult) {
			if (captureResult.properties) redactObjectValues(captureResult.properties);
			if (captureResult.$set) redactObjectValues(captureResult.$set);
			if (captureResult.$set_once) redactObjectValues(captureResult.$set_once);
		}
	} catch (e) {
		/* never block an event over sanitisation */
	}
	return captureResult;
}

if (typeof window !== 'undefined') {
	// The npm/module install (unlike the <script> snippet install most PostHog
	// docs assume) does not put the client on window — expose it so
	// posthog.debug() and friends work from the browser console.
	window.posthog = posthog;

	let isNonProduction = false;
	try {
		isNonProduction = isNonProductionHost(window.location.hostname);
	} catch (e) {
		/* noop */
	}

	posthog.init(getPostHogKey(), {
		api_host: 'https://eu.i.posthog.com',
		person_profiles: 'identified_only',
		capture_pageview: false,
		before_send: beforeSend,
		// Log every event to the browser console on localhost/staging, from
		// page load onwards (posthog.debug() by hand is too late — most form
		// events fire while the page is still loading).
		debug: isNonProduction,
		// Catch JS errors the React error boundary cannot see: throws inside
		// jQuery/DOM event handlers (the legacy forms are full of them) and
		// unhandled promise rejections from failed axios calls. These arrive
		// as $exception events and populate PostHog's Error Tracking.
		// console errors stay OFF — this app logs a lot of noisy React
		// defaultProps warnings that would drown out real failures.
		capture_exceptions: {
			capture_unhandled_errors: true,
			capture_unhandled_rejections: true,
			capture_console_errors: false,
		},
		// Clicks that do nothing — the clearest signal of a dead end in the
		// candidate/referee journeys.
		capture_dead_clicks: true,
		session_recording: {
			// Replays keep full interaction detail (clicks, focus, scrolling,
			// rage clicks, validation errors) but typed values are masked, and
			// rendered PII (names/emails shown as text, e.g. the referee
			// form's confirm-candidate-details screen) is masked via the
			// .ph-mask class. Find a specific user's session by searching
			// PostHog persons for their can_id / referee_id (the distinct_id
			// set on the form journeys).
			maskAllInputs: true,
			maskTextSelector: '.ph-mask',
		},
	});
}

export function PostHogProvider({ children }) {
	return <Provider client={posthog}>{children}</Provider>;
}
